<template>
  <div class="h-full flex flex-col md:flex-row min-h-0 overflow-y-auto md:overflow-y-visible">
    <div class="flex-1 min-w-0 min-h-[60vh] md:min-h-0 grid place-items-center relative shrink-0 md:shrink" ref="wrap">
      <div v-if="focusGrape" class="absolute top-4 left-5 panel px-4 py-2.5 flex items-center gap-3 z-10">
        <span class="w-2.5 h-2.5 rounded-full" :style="{ background: berryColor(focusGrape.color) }" />
        <div>
          <p class="text-[0.65rem] uppercase tracking-[0.18em] text-gold-bright">showing the notes of</p>
          <p class="font-display text-lg leading-tight">{{ focusGrape.name }}</p>
        </div>
        <button @click="selectGrape(null)" class="ml-2 text-ink-faint hover:text-ink text-sm">✕</button>
      </div>
      <svg ref="svg"></svg>
    </div>

    <aside class="w-full border-t md:w-80 md:border-t-0 md:border-l shrink-0 border-line md:overflow-y-auto p-5">
      <template v-if="picked">
        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint">{{ picked.path }}</p>
        <h3 class="font-display text-2xl mt-1 mb-4">{{ picked.name }}</h3>

        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Grapes showing this note</p>
        <div class="flex flex-wrap gap-1.5 mb-6">
          <button v-for="g in grapesWith" :key="g.slug" @click="selectGrape(g.slug)"
            class="chip hover:bg-wine/15 cursor-pointer">{{ g.name }}</button>
          <p v-if="!grapesWith.length" class="text-sm text-ink-dim italic">none tagged in the dataset</p>
        </div>

        <p class="text-[0.65rem] uppercase tracking-[0.18em] text-ink-faint mb-2">Compounds behind it</p>
        <div class="flex flex-col gap-2">
          <div v-for="c in compoundsWith" :key="c.slug" class="panel px-3 py-2">
            <p class="text-sm">{{ c.name }}</p>
            <p class="text-xs text-ink-faint">{{ c.chemical_class }} · “{{ c.aroma }}”</p>
          </div>
          <p v-if="!compoundsWith.length" class="text-sm text-ink-dim italic">no single compound pinned — this one's an ensemble effect</p>
        </div>
      </template>
      <div v-else class="h-full grid place-items-center text-center text-ink-dim font-display italic">
        click a slice to see which grapes<br />and molecules carry the note
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import * as d3 from "d3"
import { store, fullGrapes, selectGrape, selectedGrape, berryColor } from "../store"

const wrap = ref(null), svg = ref(null), picked = ref(null)

const focusGrape = computed(() =>
  selectedGrape.value && !selectedGrape.value.ancestor_only ? selectedGrape.value : null)

const grapesWith = computed(() => !picked.value ? [] :
  fullGrapes.value.filter(g => (g.descriptors || []).includes(picked.value.slug)))
const compoundsWith = computed(() => !picked.value ? [] :
  store.compounds.filter(c => (c.descriptor_slugs || []).includes(picked.value.slug)))

function draw() {
  const size = Math.min(wrap.value.clientWidth, wrap.value.clientHeight) - 30
  // With enough room, every leaf descriptor gets named around the rim;
  // 116 labels need ~640px of wheel before they stop colliding.
  const labelAll = !focusGrape.value && size >= 640
  const radius = size / 2 - (focusGrape.value ? 96 : labelAll ? 92 : 0)
  const root = d3.select(svg.value).attr("width", size).attr("height", size)
    .style("overflow", "visible")
  root.selectAll("*").remove()
  const g = root.append("g").attr("transform", `translate(${size / 2},${size / 2})`)

  const hierarchy = d3.hierarchy({
    name: "wine", children: store.wheel.categories.map(cat => ({
      name: cat.name, slug: cat.slug, color: cat.color,
      children: cat.subcategories.map(sub => ({
        name: sub.name, slug: sub.slug, color: cat.color,
        children: sub.descriptors.map(d => ({ name: d.name, slug: d.slug, color: cat.color, value: 1 }))
      }))
    }))
  }).sum(d => d.value || 0)

  const part = d3.partition().size([2 * Math.PI, radius])(hierarchy)
  const arc = d3.arc()
    .startAngle(d => d.x0).endAngle(d => d.x1)
    .padAngle(0.004).padRadius(radius / 2)
    .innerRadius(d => d.y0).outerRadius(d => d.y1 - 1)

  const notes = focusGrape.value ? new Set(focusGrape.value.descriptors || []) : null
  const inNotes = d => notes && d.depth === 3 && notes.has(d.data.slug)
  const subtreeHasNote = d => notes && d.descendants().some(x => x.depth === 3 && notes.has(x.data.slug))

  g.selectAll("path")
    .data(part.descendants().filter(d => d.depth))
    .join("path")
    .attr("d", arc)
    .attr("fill", d => {
      const base = d3.color(d.data.color || "#8a4b5e")
      return base.brighter(d.depth === 1 ? 0 : d.depth === 2 ? 0.45 : 0.9).formatHex()
    })
    .attr("fill-opacity", d => {
      if (!notes) return d.depth === 3 ? 0.75 : 0.9
      if (d.depth === 3) return inNotes(d) ? 1 : 0.10
      return subtreeHasNote(d) ? 0.9 : 0.18
    })
    .attr("stroke", d => inNotes(d) ? "#e6c65c" : "#120a0f")
    .attr("stroke-width", d => inNotes(d) ? 1.6 : 0.8)
    .style("cursor", d => d.depth === 3 ? "pointer" : "default")
    .on("click", (ev, d) => {
      if (d.depth !== 3) return
      picked.value = {
        slug: d.data.slug, name: d.data.name,
        path: `${d.ancestors()[2].data.name} · ${d.ancestors()[1].data.name}`,
      }
    })
    .append("title").text(d => d.ancestors().reverse().slice(1).map(a => a.data.name).join(" → "))

  // labels for cat + subcat rings
  g.selectAll("text")
    .data(part.descendants().filter(d => d.depth && d.depth < 3 && (d.x1 - d.x0) > 0.06))
    .join("text")
    .attr("transform", d => {
      const x = (d.x0 + d.x1) / 2 * 180 / Math.PI - 90
      const y = (d.y0 + d.y1) / 2
      return `rotate(${x}) translate(${y},0) rotate(${x > 90 ? 180 : 0})`
    })
    .attr("dy", "0.35em").attr("text-anchor", "middle")
    .attr("font-size", d => d.depth === 1 ? "12px" : "9px")
    .attr("fill", "#120a0f").attr("font-weight", 600)
    .attr("fill-opacity", d => notes && !subtreeHasNote(d) ? 0.3 : 1)
    .style("pointer-events", "none")
    .text(d => d.data.name)

  // name the highlighted notes just outside the rim
  if (notes) {
    g.selectAll("text.note-label")
      .data(part.descendants().filter(d => inNotes(d)))
      .join("text")
      .attr("class", "note-label")
      .attr("transform", d => {
        const a = (d.x0 + d.x1) / 2 * 180 / Math.PI - 90
        return `rotate(${a}) translate(${d.y1 + 8},0) ${a > 90 ? "rotate(180)" : ""}`
      })
      .attr("text-anchor", d => ((d.x0 + d.x1) / 2 * 180 / Math.PI - 90) > 90 ? "end" : "start")
      .attr("dy", "0.35em")
      .attr("fill", "#e6c65c")
      .attr("font-size", "11.5px")
      .style("font-family", "Fraunces, serif")
      .style("font-style", "italic")
      .style("pointer-events", "none")
      .text(d => d.data.name)
  }

  if (labelAll) {
    g.selectAll("text.leaf-label")
      .data(part.descendants().filter(d => d.depth === 3))
      .join("text")
      .attr("class", "leaf-label")
      .attr("transform", d => {
        const a = (d.x0 + d.x1) / 2 * 180 / Math.PI - 90
        return `rotate(${a}) translate(${d.y1 + 7},0) ${a > 90 ? "rotate(180)" : ""}`
      })
      .attr("text-anchor", d => ((d.x0 + d.x1) / 2 * 180 / Math.PI - 90) > 90 ? "end" : "start")
      .attr("dy", "0.35em")
      .attr("fill", "#b39c8c")
      .attr("font-size", "9.5px")
      .style("pointer-events", "none")
      .text(d => d.data.name)
  }

  g.append("text").attr("text-anchor", "middle").attr("dy", "0.1em")
    .attr("fill", "#b39c8c").attr("font-size", "13px")
    .style("font-family", "Fraunces, serif").style("font-style", "italic")
    .text("every note in wine")
}

let ro = null
onMounted(() => {
  draw()
  ro = new ResizeObserver(draw)
  ro.observe(wrap.value)
})
onBeforeUnmount(() => ro?.disconnect())

watch(() => store.selectedSlug, draw)
</script>
